import React, { PropsWithChildren, ReactElement } from 'react';
import { Card, CardActionArea, CardContent, Stack, styled, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import { CareProgramChip } from '../CareProgramChip/CareProgramChip';
import { TaskCardHeading } from './TaskCardHeading';

export interface TaskCardProps {
  icon: ReactElement;
  taskTypeLabel: string;
  taskTypeLabelColor?: string;
  taskName?: string;
  careProgramName?: string;
  isCompleted: boolean;
  onClick?: () => void;
  taskCardLabeledby?: string;
}

interface StyledTaskCardProps {
  isCompleted: boolean;
}

const StyledCardContent = styled(CardContent)({
  padding: 16,
  '&:last-child': {
    paddingBottom: 16,
  },
});

const StyledTaskCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'isCompleted',
})<StyledTaskCardProps>(({ theme, isCompleted }) => ({
  backgroundColor: isCompleted
    ? alpha(theme.palette.grey[300], 0.08)
    : theme.palette.background.paper,
  border: `1px solid ${
    isCompleted ? alpha(theme.palette.grey[300], 0.08) : alpha(theme.palette.text.primary, 0.08)
  }`,
}));

export const TaskCard = ({
  icon,
  taskTypeLabel,
  taskTypeLabelColor,
  taskName,
  careProgramName,
  isCompleted,
  onClick,
  taskCardLabeledby,
  children,
}: PropsWithChildren<TaskCardProps>) => {
  // since we are using TaskCard in a list, we need to ensure unique IDs for accessibility
  const careProgramChipId = React.useId();
  const taskHeadingId = React.useId();
  const taskNameId = React.useId();
  return (
    <StyledTaskCard
      className={`task-card${isCompleted ? '--completed' : ''}`}
      isCompleted={isCompleted}
    >
      <CardActionArea
        className="task-card__action-area"
        onClick={onClick}
        aria-roledescription="Task Card Button"
        aria-labelledby={`${taskHeadingId} ${taskName ? ` ${taskNameId}` : ''} ${taskCardLabeledby}`}
        aria-describedby={careProgramName ? careProgramChipId : undefined}
      >
        <StyledCardContent className="task-card__content">
          <Stack
            className="task-card__content-row"
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack className="task-card__content-column" spacing={1.5}>
              <TaskCardHeading
                ariaLabelId={taskHeadingId}
                labelColor={taskTypeLabelColor}
                icon={icon}
                label={taskTypeLabel}
              />
              {taskName && (
                <Typography id={taskNameId} className="task-card__task-name" variant="body1">
                  {taskName}
                </Typography>
              )}
              {careProgramName && (
                <CareProgramChip
                  ariaDescriptionId={careProgramChipId}
                  careProgramName={careProgramName}
                />
              )}
            </Stack>
            {isCompleted && <CheckIcon color="success" role="img" aria-label="Completed" />}
          </Stack>
        </StyledCardContent>
        {children}
      </CardActionArea>
    </StyledTaskCard>
  );
};
